import React, { useState, useEffect } from 'react';
import { 
  User, 
  Shield, 
  Mail, 
  Calendar, 
  Search, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  UserCog
} from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [updatingUser, setUpdatingUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Auth0 Management API configuration
  const config = {
    domain: "pag-optimus.us.auth0.com",
    clientId: "6PSiydOrLO17cBFQNVlWPRKGW6ky1BUN",
    clientSecret: "j_yvFASXENX0Y785_Kbd7RCZbFbGi5qFapXs8tN0CQvBrk9-I8DYJ_KBnZZu6BeH",
    audience: "https://pag-optimus.us.auth0.com/api/v2/",
  };

  // Role mappings
  const roleMap = {
    "rol_cVlQ62sWUUo78hWU": "Global Admin",
    "rol_jJumRGs15KKWFoNK": "Admin", 
    "rol_WkO6eoKdFCYNVr7F": "User"
  };

  const reverseRoleMap = {
    "Global Admin": "rol_cVlQ62sWUUo78hWU",
    "Admin": "rol_jJumRGs15KKWFoNK",
    "User": "rol_WkO6eoKdFCYNVr7F"
  };

  // Get current user info to determine permissions
  const currentUser = JSON.parse(localStorage.getItem('user'));
  // const canManageRoles = currentUser?.role === "Global Admin" || currentUser?.role === "Admin";
  const canManageRoles = currentUser?.role === "Global Admin";


  // Fetch access token for Management API
  const getAccessToken = async () => {
    try {
      const response = await fetch(`https://${config.domain}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: config.clientId,
          client_secret: config.clientSecret,
          audience: config.audience,
          grant_type: "client_credentials",
          scope: "read:users read:users_app_metadata read:user_idp_tokens update:users update:users_app_metadata create:users delete:users read:roles create:roles delete:roles update:roles read:role_members create:role_members delete:role_members",
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to get access token');
      }
      
      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  // Fetch all users with their roles
  const fetchUsers = async () => {
    setLoading(true);
    setError('');
  
    try {
      const token = await getAccessToken();
  
      // Fetch all users
      const usersResponse = await fetch(`${config.audience}users?per_page=100&include_totals=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!usersResponse.ok) throw new Error('Failed to fetch users');
      const usersData = await usersResponse.json();
  
      // Fetch members for each role (3 calls instead of N calls)
      const userRoleMap = {}; // userId -> { roleId, roleName }
      for (const [roleName, roleId] of Object.entries(reverseRoleMap)) {
        const res = await fetch(`${config.audience}roles/${roleId}/users?per_page=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const members = await res.json();
          members.forEach(member => {
            userRoleMap[member.user_id] = { roleId, roleName };
          });
        }
      }
  
      const usersWithRoles = usersData.users.map(user => {
        const roleInfo = userRoleMap[user.user_id];
        return {
          ...user,
          displayRole: roleInfo?.roleName || 'No Role',
          roleId: roleInfo?.roleId || null,
          username: user.user_metadata?.username || user.email?.split('@')[0] || 'N/A',
          lastLogin: user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never',
          createdAt: new Date(user.created_at).toLocaleDateString(),
        };
      });
  
      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    if (!canManageRoles) {
      setError('You do not have permission to manage roles.');
      return;
    }

    setUpdatingUser(userId);
    setError('');
    
    try {
      const token = await getAccessToken();
      const roleId = reverseRoleMap[newRole];
      
      if (!roleId) {
        throw new Error('Invalid role selected');
      }

      // First, remove all existing roles
      const currentUser = users.find(u => u.user_id === userId);
      if (currentUser.roleId) {
        await fetch(`${config.audience}users/${userId}/roles`, {
          method: 'DELETE',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            roles: [currentUser.roleId]
          }),
        });
      }

      // Then assign the new role
      const assignResponse = await fetch(`${config.audience}users/${userId}/roles`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roles: [roleId]
        }),
      });

      if (!assignResponse.ok) {
        throw new Error('Failed to assign new role');
      }

      // Update local state
      setUsers(users.map(user => 
        user.user_id === userId 
          ? { ...user, displayRole: newRole, roleId: roleId }
          : user
      ));

      setSuccessMessage(`Successfully updated role for ${currentUser.username}`);
      setTimeout(() => setSuccessMessage(''), 3000);

    } catch (error) {
      console.error('Error updating user role:', error);
      setError('Failed to update user role. Please try again.');
    } finally {
      setUpdatingUser(null);
    }
  };

  // Filter users based on search query and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = selectedRole === 'all' || user.displayRole === selectedRole;
    
    return matchesSearch && matchesRole;
  });

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Global Admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'Admin':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'User':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-6 h-6 animate-spin text-[#a32b13]" />
          <span className="text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              User Management
            </h1>
            <p className="text-gray-600 mt-1">Manage user accounts and role assignments</p>
          </div>
         
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
          <span className="text-green-700">{successMessage}</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a32b13] focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <div className="sm:w-48">
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#a32b13] focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="Global Admin">Global Admin</option>
              <option value="Admin">Admin</option>
              <option value="User">User</option>
              <option value="No Role">No Role</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                {canManageRoles && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={canManageRoles ? 5 : 4} className="px-6 py-8 text-center text-gray-500">
                    {searchQuery || selectedRole !== 'all' ? 'No users found matching your filters.' : 'No users found.'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-[#a32b13] flex items-center justify-center text-white font-semibold mr-3">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.username}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-3 h-3 mr-1" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.displayRole)}`}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user.displayRole}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {user.lastLogin}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt}
                    </td>
                    {canManageRoles && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.displayRole}
                          onChange={(e) => updateUserRole(user.user_id, e.target.value)}
                          disabled={
                            updatingUser === user.user_id || 
                            (user.email === currentUser?.email && currentUser?.role !== "Global Admin") ||
                            (currentUser?.role === "Global Admin" && user.displayRole === "Global Admin" && user.email !== currentUser?.email)
                          }
                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#a32b13] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {/* <option value="Global Admin">Global Admin</option> */}
                          <option value="Admin">Admin</option>
                          <option value="User">User</option>
                        </select>
                        {updatingUser === user.user_id && (
                          <RefreshCw className="w-4 h-4 animate-spin text-[#a32b13] ml-2 inline" />
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="mt-6 text-sm text-gray-500 text-center">
        Showing {filteredUsers.length} of {users.length} users
      </div>
    </div>
  );
};

export default UserManagement;